import { Button } from "@/components/ui/button";
import { NotebookPen, SaveAll } from "lucide-react";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import axios from "axios";
import { toast } from "react-toastify";

export const FormNotesPatientFilesComponents = ({
  sujet,
  notes,
  seanceId,
  fiable,
}: {
  sujet: string;
  notes: string;
  seanceId: number;
  fiable: boolean;
}) => {
  const formSchema = z.object({
    notes: z.string().optional(),
    sujet: z.string().max(40),
    fiable: z.boolean(),
  });
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      notes: notes,
      sujet: sujet,
      fiable: fiable,
    },
  });
  const handleSubmite = (data: z.infer<typeof formSchema>) => {
    try {
      axios.put(`/practitioner/session/${seanceId}`, data)
        .then(() => {
          toast.success("La séance a bien été modifiée");
        })
        .catch(() => {
          toast.error("Une erreur est survenue");
        });
    } catch (error) {
      console.error("An unexpected error occurred:", error);
      toast.error("Une erreur inattendue est survenue");
    }
  };

  const isSessionFiableClassColor = () => {
    if (fiable) {
      return "bg-green-200";
    } else {
      return "bg-red-200";
    }
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" aria-label="Dupliquer"  title={notes} className={isSessionFiableClassColor()} >
          <NotebookPen className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Notes de la séance - Séance ID : {seanceId}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmite)}>
            <div className="flex items-center space-x-2">
              <div className="grid flex-1 gap-2">
                <FormField
                  control={form.control}
                  name="sujet"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Thème de la séance</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Thème de la séance"
                          className="resize-none"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Notes</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Ajouter un commentaire à la séance"
                          className="resize-none"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  name="fiable"
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                        <FormLabel className="ml-1">
                          Les résultats de la séance sont fiables{" "}
                        </FormLabel>
                    </FormItem>
                  )}
                />
              </div>
            </div>
            <DialogClose asChild>
            <Button type="submit" className="mt-6">
              <SaveAll className="mr-2 h-4 w-4" />
              Modifier la note
            </Button>
            </DialogClose>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
